import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { ApolloError } from 'apollo-client';
import b_ from 'b_';
import cn from 'classnames';
import { ErrorDescription, formatApolloError } from 'shared/utils/formatError';
import Notification from '../Notification';

import './index.scss';

const b = b_.with('submit-button');

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  submitting?: boolean;
  error?: ApolloError | ErrorDescription | string;
  primary?: boolean;
  notificationWidth?: number;
}

const formatError = (
  t: TFunction,
  error?: ApolloError | ErrorDescription | string
): string | undefined => {
  if (error instanceof ApolloError) {
    const nestedError = formatApolloError(error);
    return nestedError ? formatError(t, nestedError) : undefined;
  }
  if (error instanceof ErrorDescription) {
    return t(error.message, error.details);
  }
  if (typeof error === 'string') {
    return t(error);
  }
  return;
};

const SubmitButton: React.FC<Props> = ({
  submitting,
  error,
  primary,
  className,
  disabled,
  children,
  notificationWidth,
  ...props
}) => {
  const { t } = useTranslation();
  const [isDisabled, toggleDisabled] = React.useState(true);

  React.useEffect(() => {
    toggleDisabled(false);
  });

  return (
    <button
      className={cn(b({ submitting, primary }), className)}
      disabled={submitting || disabled || isDisabled}
      data-testid="submit-btn"
      {...props}
    >
      {children}
      {error && (
        <Notification notificationWidth={notificationWidth} level="error" position="bottom">
          {formatError(t, error)}
        </Notification>
      )}
    </button>
  );
};

export default SubmitButton;
